if (!customElements.get('vc-media-gallery')) {
  customElements.define(
    'vc-media-gallery',
    class VCMediaGallery extends HTMLElement {
      constructor() {
        super();
        // Mobile swipe state
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.touchStartY = 0;
        this.touchEndY = 0;
        this.isMobile = window.innerWidth < 750;
        this.thumbnailScrollStart = 0;
        this.thumbnailBaseTransform = 0;
        this.currentIndex = 0;
        this.scrollOffset = 0;
        this.preloadedImageSrcs = new Set();
        this._scheduledTasks = new Set();
        this._zoomListenersBound = false;
        this._mobileSwipeInitialized = false;
        this._stickyBarInitialized = false;
        this._zoomImageLoaded = false;
        this._zoomImagePreloading = false;
      }

      connectedCallback() {
        // Query elements after DOM is ready
        this.elements = {
          thumbnailsContainer: this.querySelector('[id^="vc-product-thumbnails-container"]'),
          mediaContainer: this.querySelector('.vc-product__media'),
          thumbnails: this.querySelectorAll('.vc-product__thumbnail'),
          mediaWrapper: this.querySelector('[id^="vc-product-media-wrapper"]'),
          dotsContainer: this.querySelector('[id^="vc-product-dots"]'),
          dots: this.querySelectorAll('.vc-product__dot'),
          thumbnailOverflow: this.querySelector('[data-thumbnail-overflow]'),
        };

        this.sectionId = this.dataset.sectionId;
        this.visibleThumbnails = parseInt(this.dataset.visibleThumbnails) || 5;
        this.thumbnailHeight = parseInt(this.dataset.thumbnailSize) || 156;
        this.thumbnailMargin = parseInt(this.dataset.thumbnailMargin) || 12;
        this.zoomEnabled = this.dataset.zoomEnabled === 'true';
        this.zoomLevel = parseFloat(this.dataset.zoomLevel) || 3;
        this.mediaData = this.getMediaData();
        this.currentIndex = this.getInitialIndex();

        this.initThumbnailEvents();
        this.initNavigationButtons();
        this.initThumbnailOverflowIndicator();
        this.initKeyboardNavigation();
        this.initDotIndicators();
        this.syncResponsiveEnhancements();

        this.elements.thumbnails.forEach((thumb, index) => {
          thumb.classList.toggle('vc-product__thumbnail--active', index === this.currentIndex);
        });
        this.updateDotIndicators();
        this.scrollToThumbnail(this.currentIndex);

        // Preload nearby images after first paint instead of competing with hero media.
        this.scheduleAdjacentPreload(this.currentIndex);

        // Subscribe to variant changes
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );

        this._onResize = debounce(this.handleResize.bind(this), 200);
        window.addEventListener('resize', this._onResize);
      }

      runWhenIdle(callback, timeout = 800) {
        if (typeof callback !== 'function') return null;

        let task;
        const complete = () => {
          this._scheduledTasks.delete(task);
          callback();
        };

        if (typeof window.requestIdleCallback === 'function') {
          task = { type: 'idle', handle: window.requestIdleCallback(complete, { timeout }) };
        } else {
          task = { type: 'timeout', handle: window.setTimeout(complete, Math.min(timeout, 250)) };
        }

        this._scheduledTasks.add(task);
        return task;
      }

      cancelScheduledTask(task) {
        if (!task) return;

        if (task.type === 'idle' && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(task.handle);
        } else {
          window.clearTimeout(task.handle);
        }

        this._scheduledTasks.delete(task);
      }

      clearScheduledTasks() {
        this._scheduledTasks.forEach((task) => this.cancelScheduledTask(task));
      }

      syncResponsiveEnhancements() {
        const sectionRoot = this.getSectionRoot();
        const shouldEnableSwipe = window.innerWidth < 750 && this.getVisibleMediaIndexes().length > 1;
        const shouldEnableZoom = this.zoomEnabled && window.innerWidth >= 990;
        const shouldEnableStickyBar =
          window.innerWidth < 750 && Boolean(sectionRoot.querySelector(`#vc-product-sticky-bar-${this.sectionId}`));

        if (shouldEnableSwipe) {
          this.initMobileSwipe();
        } else {
          this.removeMobileSwipeListeners();
        }

        if (shouldEnableZoom) {
          this.initZoom();
        } else {
          this.destroyZoom();
        }

        if (shouldEnableStickyBar) {
          this.initStickyBar();
        } else {
          this.destroyStickyBar();
        }
      }

      getAdjacentPreloadIndexes(index) {
        const visibleIndexes = this.getVisibleMediaIndexes();
        const currentPosition = visibleIndexes.indexOf(index);
        const indexesToPreload = [];

        if (currentPosition === -1) return indexesToPreload;

        [-1, 1].forEach((direction) => {
          const preloadIndex = visibleIndexes[currentPosition + direction];
          if (preloadIndex !== undefined) {
            indexesToPreload.push(preloadIndex);
          }
        });

        return indexesToPreload;
      }

      scheduleAdjacentPreload(index) {
        this.cancelScheduledTask(this._adjacentPreloadTask);
        this._adjacentPreloadTask = this.runWhenIdle(() => {
          this._adjacentPreloadTask = null;
          this.preloadAdjacentImages(index);
        }, 900);
      }

      // Preload adjacent images only after the main product media is ready.
      preloadAdjacentImages(index) {
        this.getAdjacentPreloadIndexes(index).forEach((preloadIndex) => {
          const media = this.mediaData[preloadIndex];
          if (!media || media.type !== 'image' || !media.src || this.preloadedImageSrcs.has(media.src)) {
            return;
          }

          this.preloadedImageSrcs.add(media.src);

          const img = new Image();
          if (media.srcset) img.srcset = media.srcset;
          if (media.sizes) img.sizes = media.sizes;
          img.src = media.src;
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.removeMobileSwipeListeners();
        this.destroyZoom();
        this.destroyStickyBar();
        if (this._onThumbnailOverflowScroll && this.elements.thumbnailsContainer) {
          this.elements.thumbnailsContainer.removeEventListener('scroll', this._onThumbnailOverflowScroll);
        }
        if (this._onThumbnailOverflowClick && this.elements.thumbnailOverflow) {
          this.elements.thumbnailOverflow.removeEventListener('click', this._onThumbnailOverflowClick);
        }
        this.clearScheduledTasks();
        if (this._onResize) {
          window.removeEventListener('resize', this._onResize);
        }
      }

      getMediaData() {
        const mediaDataScript = this.querySelector('#vc-media-data');
        if (mediaDataScript) {
          try {
            return JSON.parse(mediaDataScript.textContent);
          } catch (e) {
            // Error handled silently in production - media data parse failed
          }
        }
        return [];
      }

      getSectionRoot() {
        return this.closest('product-info') || document.getElementById(`MainProduct-${this.sectionId}`) || document;
      }

      getInitialIndex() {
        const activeThumbnail = this.querySelector('.vc-product__thumbnail--active');
        if (activeThumbnail?.dataset?.index !== undefined) {
          const thumbnailIndex = parseInt(activeThumbnail.dataset.index, 10);
          if (!Number.isNaN(thumbnailIndex)) {
            return thumbnailIndex;
          }
        }

        const activeMediaId = this.querySelector('.vc-product__media [data-media-id]')?.dataset?.mediaId;
        if (activeMediaId) {
          const mediaIndex = this.mediaData.findIndex((media) => media.id === String(activeMediaId));
          if (mediaIndex >= 0) {
            return mediaIndex;
          }
        }

        return 0;
      }

      getVisibleMediaIndexes() {
        const visibleIndexes = Array.from(this.elements.thumbnails || [])
          .filter((thumbnail) => !thumbnail.hidden && thumbnail.style.display !== 'none')
          .map((thumbnail) => parseInt(thumbnail.dataset.index, 10))
          .filter((index) => !Number.isNaN(index));

        return visibleIndexes.length
          ? visibleIndexes
          : this.mediaData.map((_, index) => index);
      }

      applyVisibleMediaIds(visibleMediaIds) {
        if (!Array.isArray(visibleMediaIds)) return;

        const normalizedVisibleMediaIds = [...new Set(
          visibleMediaIds
            .map((mediaId) => String(mediaId || '').trim())
            .filter(Boolean)
        )];

        if (normalizedVisibleMediaIds.length === 0) return;

        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaId = String(thumbnail.dataset.mediaId || '').trim();
          const isVisible = normalizedVisibleMediaIds.includes(mediaId);

          thumbnail.hidden = !isVisible;
          thumbnail.style.display = isVisible ? '' : 'none';

          if (!isVisible) {
            thumbnail.classList.remove('vc-product__thumbnail--active');
            thumbnail.setAttribute('aria-selected', 'false');
            thumbnail.tabIndex = -1;
          }
        });

        this.elements.dots.forEach((dot) => {
          const mediaId = String(dot.dataset.mediaId || '').trim();
          const isVisible = normalizedVisibleMediaIds.includes(mediaId);

          dot.hidden = !isVisible;
          dot.style.display = isVisible ? '' : 'none';

          if (!isVisible) {
            dot.classList.remove('vc-product__dot--active');
          }
        });

        this.scrollOffset = 0;

        if (this.elements.thumbnailsContainer) {
          this.elements.thumbnailsContainer.scrollLeft = 0;
        }
      }

      getAdjacentVisibleIndex(direction) {
        const visibleIndexes = this.getVisibleMediaIndexes();
        if (!visibleIndexes.length) return this.currentIndex;

        const currentPosition = visibleIndexes.indexOf(this.currentIndex);
        if (currentPosition === -1) {
          return direction >= 0
            ? visibleIndexes[0]
            : visibleIndexes[visibleIndexes.length - 1];
        }

        const nextPosition = Math.max(
          0,
          Math.min(visibleIndexes.length - 1, currentPosition + direction)
        );

        return visibleIndexes[nextPosition];
      }

      getYouTubeVideoId(url) {
        if (!url) return null;

        const patterns = [
          /youtu\.be\/([^?&#/]+)/i,
          /youtube\.com\/shorts\/([^?&#/]+)/i,
          /youtube\.com\/embed\/([^?&#/]+)/i,
          /youtube-nocookie\.com\/embed\/([^?&#/]+)/i,
          /youtube\.com\/live\/([^?&#/]+)/i,
          /[?&]v=([^?&#/]+)/i,
        ];

        for (const pattern of patterns) {
          const match = url.match(pattern);
          if (match?.[1]) return match[1];
        }

        return null;
      }

      getVideoDescriptor(videoData) {
        if (!videoData?.videoSrc) {
          return {
            provider: 'file',
            videoId: null,
            videoSrc: null,
            embedSrc: null,
            previewSrc: null,
          };
        }

        const videoId = videoData.videoId || this.getYouTubeVideoId(videoData.videoSrc);
        const provider = videoData.videoProvider || (videoId ? 'youtube' : 'file');

        return {
          provider,
          videoId,
          videoSrc: videoData.videoSrc,
          embedSrc:
            videoData.embedSrc ||
            (videoId ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1&playsinline=1&rel=0` : null),
          previewSrc:
            videoData.previewSrc ||
            (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null),
        };
      }

      initThumbnailEvents() {
        this.elements.thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => this.switchMedia(index));
        });
      }

      initNavigationButtons() {
        const upBtn = this.querySelector('.vc-product__thumb-nav--up');
        const downBtn = this.querySelector('.vc-product__thumb-nav--down');

        upBtn?.addEventListener('click', () => this.scrollThumbnails(-1));
        downBtn?.addEventListener('click', () => this.scrollThumbnails(1));

        this.updateNavButtons();
      }

      initThumbnailOverflowIndicator() {
        if (!this.elements.thumbnailsContainer || !this.elements.thumbnailOverflow) return;

        this._onThumbnailOverflowScroll = () => {
          this.updateThumbnailOverflowIndicator();
        };

        this._onThumbnailOverflowClick = () => {
          const scrollAmount = Math.max(
            this.elements.thumbnailsContainer.clientWidth - 120,
            this.elements.thumbnailsContainer.clientWidth * 0.72
          );
          this.elements.thumbnailsContainer.scrollBy({
            left: scrollAmount,
            behavior: 'smooth',
          });
        };

        this.elements.thumbnailsContainer.addEventListener('scroll', this._onThumbnailOverflowScroll, {
          passive: true,
        });
        this.elements.thumbnailOverflow.addEventListener('click', this._onThumbnailOverflowClick);
        this.updateThumbnailOverflowIndicator();
      }

      usesHorizontalThumbnailScroll() {
        if (window.innerWidth < 750) return true;

        const thumbnailList = this.querySelector('.vc-product__thumbnails');
        if (!thumbnailList) return false;

        const styles = window.getComputedStyle(thumbnailList);
        return styles.flexDirection === 'row';
      }

      updateThumbnailOverflowIndicator() {
        const indicator = this.elements.thumbnailOverflow;
        const container = this.elements.thumbnailsContainer;
        const wrapper = this.querySelector('.vc-product__thumbnails-wrapper');

        if (!indicator || !container || !wrapper || window.innerWidth < 990 || !this.usesHorizontalThumbnailScroll()) {
          if (indicator) {
            indicator.hidden = true;
            indicator.textContent = '';
            indicator.style.left = '';
            indicator.style.top = '';
            indicator.style.width = '';
            indicator.style.height = '';
          }
          return;
        }

        const visibleThumbnails = Array.from(this.elements.thumbnails || []).filter(
          (thumbnail) => !thumbnail.hidden && thumbnail.style.display !== 'none'
        );

        if (visibleThumbnails.length <= 1) {
          indicator.hidden = true;
          indicator.textContent = '';
          indicator.style.left = '';
          indicator.style.top = '';
          indicator.style.width = '';
          indicator.style.height = '';
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const fullyVisibleThumbnails = visibleThumbnails.filter((thumbnail) => {
          const rect = thumbnail.getBoundingClientRect();
          return rect.left >= containerRect.left + 1 && rect.right <= containerRect.right - 1;
        });
        const partiallyVisibleThumbnails = visibleThumbnails.filter((thumbnail) => {
          const rect = thumbnail.getBoundingClientRect();
          return rect.right > containerRect.left + 1 && rect.left < containerRect.right - 1;
        });
        const anchorThumbnail =
          partiallyVisibleThumbnails[partiallyVisibleThumbnails.length - 1] ||
          fullyVisibleThumbnails[fullyVisibleThumbnails.length - 1];
        const anchorIndex = anchorThumbnail ? visibleThumbnails.indexOf(anchorThumbnail) : -1;
        const remainingCount =
          anchorIndex >= 0 ? Math.max(0, visibleThumbnails.length - anchorIndex - 1) : 0;

        if (remainingCount > 0 && anchorThumbnail) {
          const anchorRect = anchorThumbnail.getBoundingClientRect();
          indicator.hidden = false;
          indicator.textContent = `${remainingCount}+`;
          indicator.setAttribute('aria-label', `Show ${remainingCount} more product images`);
          indicator.style.left = `${anchorRect.left - wrapperRect.left}px`;
          indicator.style.top = `${anchorRect.top - wrapperRect.top}px`;
          indicator.style.width = `${anchorRect.width}px`;
          indicator.style.height = `${anchorRect.height}px`;
        } else {
          indicator.hidden = true;
          indicator.textContent = '';
          indicator.style.left = '';
          indicator.style.top = '';
          indicator.style.width = '';
          indicator.style.height = '';
        }
      }

      initKeyboardNavigation() {
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            this.switchMedia(this.getAdjacentVisibleIndex(-1));
          } else if (e.key === 'ArrowRight') {
            this.switchMedia(this.getAdjacentVisibleIndex(1));
          } else if (e.key === 'ArrowUp') {
            this.scrollThumbnails(-1);
          } else if (e.key === 'ArrowDown') {
            this.scrollThumbnails(1);
          }
        });
      }

      updateMainMedia(media) {
        if (!this.elements.mediaContainer || !media) return;

        if (media.type === 'image') {
          this.elements.mediaContainer.innerHTML = '';
          const img = document.createElement('img');
          img.src = media.src;
          img.srcset = media.srcset || '';
          img.sizes = media.sizes || '100vw';
          img.alt = media.alt || '';
          img.dataset.mediaId = media.id;
          img.className = 'vc-main-media-image';
          img.loading = 'eager';
          img.decoding = 'async';
          img.fetchPriority = 'auto';
          img.draggable = false;
          this.elements.mediaContainer.appendChild(img);
        } else if (media.type === 'video') {
          this.elements.mediaContainer.innerHTML = '';
          if (!media.videoSrc) return;

          const descriptor = this.getVideoDescriptor(media);

          if (descriptor.provider === 'youtube' && descriptor.embedSrc) {
            const iframe = document.createElement('iframe');
            iframe.src = descriptor.embedSrc;
            iframe.dataset.mediaId = media.id;
            iframe.className = 'js-youtube global-media-settings global-media-settings--no-full-screen';
            iframe.title = media.alt || 'Product video';
            iframe.allow =
              'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.allowFullscreen = true;
            iframe.loading = 'eager';
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = '0';
            this.elements.mediaContainer.appendChild(iframe);
            return;
          }

          const video = document.createElement('video');
          video.src = descriptor.videoSrc;
          video.controls = true;
          video.preload = 'metadata';
          video.playsInline = true;
          video.dataset.mediaId = media.id;
          video.className = 'global-media-settings global-media-settings--no-full-screen';
          video.style.width = '100%';
          video.style.height = '100%';
          video.setAttribute('playsinline', '');
          this.elements.mediaContainer.appendChild(video);
        } else if (media.type === 'model') {
          const productModel = document.createElement('product-model');
          productModel.dataset.productId = this.dataset.productId;
          productModel.className = 'global-media-settings';
          productModel.style.paddingTop = '100%';
          const viewer = document.createElement('model-viewer');
          viewer.src = media.src;
          viewer.setAttribute('auto-rotate', '');
          viewer.setAttribute('camera-controls', '');
          productModel.appendChild(viewer);
          this.elements.mediaContainer.appendChild(productModel);
        }
      }

      scrollThumbnails(direction) {
        if (this.usesHorizontalThumbnailScroll()) {
          const container = this.elements.thumbnailsContainer;
          const firstThumbnail = this.elements.thumbnails[0];
          if (!container || !firstThumbnail) return;

          const scrollStep = firstThumbnail.offsetWidth + this.thumbnailMargin;
          container.scrollBy({
            left: direction * scrollStep,
            behavior: 'smooth',
          });
          return;
        }

        const newIndex = this.scrollOffset + direction;
        const maxOffset = Math.max(0, this.getVisibleMediaIndexes().length - this.visibleThumbnails);

        if (newIndex >= 0 && newIndex <= maxOffset) {
          this.scrollOffset = newIndex;
          this.updateThumbnailsScroll();
        }
      }

      scrollToThumbnail(index) {
        // Mobile and bottom-row desktop layouts: use horizontal scroll
        if (this.usesHorizontalThumbnailScroll()) {
          const thumbnail = this.elements.thumbnails[index];
          if (!thumbnail) return;

          const container = this.elements.thumbnailsContainer;
          if (!container) return;

          // Calculate scroll position to center the thumbnail
          const containerWidth = container.offsetWidth;
          const thumbnailWidth = thumbnail.offsetWidth;
          const gap = 12; // 0.75rem in pixels
          const scrollLeft = thumbnail.offsetLeft - (containerWidth / 2) + (thumbnailWidth / 2);

          // Temporarily enable smooth scroll for snap animation
          container.style.scrollBehavior = 'smooth';
          container.scrollTo({
            left: scrollLeft,
          });

          // Reset to auto after animation completes
          setTimeout(() => {
            container.style.scrollBehavior = 'auto';
          }, 300);
        } else {
          // Desktop: Use vertical transform
          const visibleIndexes = this.getVisibleMediaIndexes();
          const visiblePosition = visibleIndexes.indexOf(index);
          if (visiblePosition === -1) return;

          const maxOffset = Math.max(0, visibleIndexes.length - this.visibleThumbnails);

          if (visiblePosition < this.scrollOffset) {
            this.scrollOffset = visiblePosition;
          } else if (visiblePosition >= this.scrollOffset + this.visibleThumbnails) {
            this.scrollOffset = Math.min(maxOffset, visiblePosition - this.visibleThumbnails + 1);
          }
          this.updateThumbnailsScroll();
        }
      }

      updateThumbnailsScroll() {
        // Get the inner thumbnails list element (not the container)
        const thumbnailsList = this.querySelector('.vc-product__thumbnails');
        if (!thumbnailsList) return;

        if (this.usesHorizontalThumbnailScroll()) {
          thumbnailsList.style.transform = 'none';
          this.updateNavButtons();
          return;
        }

        // Dynamically get actual thumbnail size from rendered element
        const firstThumbnail = this.elements.thumbnails[0];
        const actualThumbnailHeight = firstThumbnail ? firstThumbnail.offsetHeight : this.thumbnailHeight;
        const thumbnailTotal = actualThumbnailHeight + this.thumbnailMargin;
        
        thumbnailsList.style.transform = `translateY(-${this.scrollOffset * thumbnailTotal}px)`;
        this.updateNavButtons();
      }

      updateNavButtons() {
        const upBtn = this.querySelector('.vc-product__thumb-nav--up');
        const downBtn = this.querySelector('.vc-product__thumb-nav--down');

        if (this.usesHorizontalThumbnailScroll()) {
          upBtn?.classList.add('vc-product__thumb-nav--disabled');
          downBtn?.classList.add('vc-product__thumb-nav--disabled');
          this.updateThumbnailOverflowIndicator();
          return;
        }

        const maxOffset = Math.max(0, this.getVisibleMediaIndexes().length - this.visibleThumbnails);

        if (this.scrollOffset > maxOffset) {
          this.scrollOffset = maxOffset;
        }

        upBtn?.classList.toggle('vc-product__thumb-nav--disabled', this.scrollOffset === 0);
        downBtn?.classList.toggle('vc-product__thumb-nav--disabled', this.scrollOffset >= maxOffset);
        this.updateThumbnailOverflowIndicator();
      }

      // Zoom functionality - Hover to magnify (Amazon/Best Practice style)
      initZoom() {
        if (!this.zoomEnabled || !this.elements.mediaContainer || this._zoomListenersBound) {
          return;
        }

        if (window.innerWidth < 990) return;

        this._boundZoomEnter = (e) => {
          if (window.innerWidth >= 990 && this.zoomEnabled) {
            this.ensureZoomImageReady();
            this.activateZoom(e);
          }
        };
        this._boundZoomMove = (e) => {
          if (this._zoomOverlay && window.innerWidth >= 990) {
            this.moveZoomOverlay(e);
          }
        };
        this._boundZoomLeave = () => {
          this.deactivateZoom();
        };

        this.elements.mediaContainer.addEventListener('mouseenter', this._boundZoomEnter, { passive: true });
        this.elements.mediaContainer.addEventListener('mousemove', this._boundZoomMove);
        this.elements.mediaContainer.addEventListener('mouseleave', this._boundZoomLeave);
        this._zoomListenersBound = true;
      }

      destroyZoom() {
        if (!this.elements.mediaContainer) {
          this.deactivateZoom();
          this._zoomListenersBound = false;
          return;
        }

        if (this._boundZoomEnter) {
          this.elements.mediaContainer.removeEventListener('mouseenter', this._boundZoomEnter);
          this._boundZoomEnter = null;
        }
        if (this._boundZoomMove) {
          this.elements.mediaContainer.removeEventListener('mousemove', this._boundZoomMove);
          this._boundZoomMove = null;
        }
        if (this._boundZoomLeave) {
          this.elements.mediaContainer.removeEventListener('mouseleave', this._boundZoomLeave);
          this._boundZoomLeave = null;
        }

        this.deactivateZoom();
        this._zoomListenersBound = false;
      }

      ensureZoomImageReady() {
        const image = this.elements.mediaContainer?.querySelector('.vc-main-media-image');
        if (!image) return;

        let zoomImageSrc = image.src;
        if (zoomImageSrc.includes('width=')) {
          zoomImageSrc = zoomImageSrc.replace(/width=\d+/, 'width=2000');
        }

        if (this._preloadedZoomSrc === zoomImageSrc && (this._zoomImageLoaded || this._zoomImagePreloading)) {
          return;
        }

        this._zoomImageLoaded = false;
        this._zoomImagePreloading = true;
        this._preloadedZoomSrc = zoomImageSrc;

        const preloadImg = new Image();
        preloadImg.onload = () => {
          if (this._preloadedZoomSrc !== zoomImageSrc) return;

          this._zoomImageLoaded = true;
          this._zoomImagePreloading = false;
          // If overlay is waiting, show it now
          if (this._pendingOverlay) {
            this._pendingOverlay.style.opacity = '1';
            this._pendingOverlay = null;
          }
        };
        preloadImg.onerror = () => {
          if (this._preloadedZoomSrc === zoomImageSrc) {
            this._zoomImagePreloading = false;
          }
        };
        preloadImg.src = zoomImageSrc;
      }

      activateZoom(event) {
        const image = this.elements.mediaContainer?.querySelector('.vc-main-media-image');
        if (!image || this._zoomOverlay) return;
        
        // Calculate zoom size
        const containerRect = this.elements.mediaContainer.getBoundingClientRect();
        const zoomWidth = containerRect.width * this.zoomLevel;
        const zoomHeight = containerRect.height * this.zoomLevel;
        
        // Get high-resolution image URL
        let zoomImageSrc = this._preloadedZoomSrc || image.src;
        if (!this._preloadedZoomSrc && zoomImageSrc.includes('width=')) {
          zoomImageSrc = zoomImageSrc.replace(/width=\d+/, 'width=2000');
        }
        
        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'vc-magnify-overlay zoom';
        overlay.setAttribute('aria-hidden', 'true');
        
        // Start with opacity 0 if image not loaded yet
        const initialOpacity = this._zoomImageLoaded ? '1' : '0';
        
        overlay.setAttribute('style', `
          display: block !important;
          visibility: visible !important;
          opacity: ${initialOpacity} !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          z-index: 9999 !important;
          cursor: crosshair !important;
          background-color: rgb(var(--color-background, 255, 255, 255)) !important;
          background-image: url('${/^https?:|^data:image\//.test(zoomImageSrc) ? zoomImageSrc : ''}') !important;
          background-repeat: no-repeat !important;
          background-size: ${zoomWidth}px ${zoomHeight}px !important;
          pointer-events: none !important;
          transition: opacity 0.2s ease !important;
        `);
        
        // Store reference
        this._zoomOverlay = overlay;
        this._zoomWidth = zoomWidth;
        this._zoomHeight = zoomHeight;
        
        // If image not loaded, store reference to show later
        if (!this._zoomImageLoaded) {
          this._pendingOverlay = overlay;
        }
        
        // Insert overlay
        this.elements.mediaContainer.appendChild(overlay);
        
        // Position based on initial mouse position
        this.moveZoomOverlay(event);
      }
      
      moveZoomOverlay(event) {
        if (!this._zoomOverlay) return;
        
        const container = this.elements.mediaContainer.getBoundingClientRect();
        const xPosition = event.clientX - container.left;
        const yPosition = event.clientY - container.top;
        
        // Calculate percentage position (clamped to 0-100)
        const xPercent = Math.max(0, Math.min(100, (xPosition / container.width) * 100));
        const yPercent = Math.max(0, Math.min(100, (yPosition / container.height) * 100));
        
        this._zoomOverlay.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
      }
      
      deactivateZoom() {
        if (this._zoomOverlay) {
          this._zoomOverlay.remove();
          this._zoomOverlay = null;
        }
        this._pendingOverlay = null;
      }

      updateZoom() {
        this.deactivateZoom();
        this._zoomImageLoaded = false;
        this._zoomImagePreloading = false;
        this._preloadedZoomSrc = null;
      }

      handleResize() {
        this.updateThumbnailsScroll();

        // Update mobile state and scroll bounds
        this.isMobile = window.innerWidth < 750;
        if (this._thumbnailScrollBounds) {
          this._updateScrollBounds();
        }

        this.syncResponsiveEnhancements();
      }

      setCustomVideoData(videoData) {
        const customVideoIndex = this.mediaData.findIndex((media) => media.id === 'custom-video');
        if (customVideoIndex < 0) return;

        const descriptor = this.getVideoDescriptor(videoData);
        const hasVideo = Boolean(descriptor.videoSrc);
        const currentMedia = this.mediaData[customVideoIndex] || {};

        this.mediaData[customVideoIndex] = {
          ...currentMedia,
          id: 'custom-video',
          type: 'video',
          alt: videoData?.alt || currentMedia.alt || '',
          videoSrc: hasVideo ? descriptor.videoSrc : null,
          videoProvider: hasVideo ? descriptor.provider : null,
          videoId: hasVideo ? descriptor.videoId : null,
          embedSrc: hasVideo ? descriptor.embedSrc : null,
          previewSrc: hasVideo ? descriptor.previewSrc : null,
          isCustomVideo: true,
        };

        const customThumbnail = this.querySelector('.vc-product__thumbnail[data-media-id="custom-video"]');
        const customDot = this.querySelector('.vc-product__dot[data-media-id="custom-video"]');
        const previewContainer = customThumbnail?.querySelector('[data-custom-video-preview]');

        if (previewContainer) {
          previewContainer.innerHTML = '';

          if (hasVideo) {
            if (descriptor.provider === 'youtube' && descriptor.previewSrc) {
              const previewImage = document.createElement('img');
              previewImage.src = descriptor.previewSrc;
              previewImage.alt = videoData?.alt || 'Product video';
              previewImage.loading = 'lazy';
              previewImage.width = 200;
              previewImage.height = 200;
              previewImage.decoding = 'async';
              previewContainer.appendChild(previewImage);
            } else {
              const previewVideo = document.createElement('video');
              previewVideo.src = descriptor.videoSrc;
              previewVideo.muted = true;
              previewVideo.preload = 'metadata';
              previewVideo.playsInline = true;
              previewVideo.setAttribute('playsinline', '');
              previewVideo.setAttribute('aria-hidden', 'true');
              previewContainer.appendChild(previewVideo);
            }
          }
        }

        if (customThumbnail) {
          customThumbnail.hidden = !hasVideo;
          customThumbnail.style.display = hasVideo ? '' : 'none';

          if (!hasVideo) {
            customThumbnail.classList.remove('vc-product__thumbnail--active');
            customThumbnail.setAttribute('aria-selected', 'false');
            customThumbnail.tabIndex = -1;
          }
        }

        if (customDot) {
          customDot.hidden = !hasVideo;
          customDot.style.display = hasVideo ? '' : 'none';

          if (!hasVideo) {
            customDot.classList.remove('vc-product__dot--active');
          }
        }

        if (!hasVideo && this.currentIndex === customVideoIndex) {
          const fallbackIndex = this.getVisibleMediaIndexes().find((index) => index !== customVideoIndex);
          if (fallbackIndex !== undefined) {
            this.switchMedia(fallbackIndex);
            return;
          }
        }

        this.updateNavButtons();
      }

      handleVariantChange(event) {
        const payload = event?.data || {};
        if (!payload.sectionId || String(payload.sectionId) !== String(this.sectionId)) return;

        const variant = payload.variant || null;
        this.applyVisibleMediaIds(payload.visibleMediaIds);

        const fallbackCustomVideo = this.mediaData.find(
          (media) => media.id === 'custom-video' && media.videoSrc
        );
        const targetMediaId = payload.activeMediaId || variant?.featured_media?.id || fallbackCustomVideo?.id;
        const requestedMediaIndex = this.mediaData.findIndex((m) => m.id === String(targetMediaId));
        const visibleIndexes = this.getVisibleMediaIndexes();
        const mediaIndex = visibleIndexes.includes(requestedMediaIndex)
          ? requestedMediaIndex
          : visibleIndexes[0];

        if (mediaIndex >= 0) {
          this.switchMedia(mediaIndex);
        }

        this.updateThumbnailsScroll();
        this.updateNavButtons();
        this.syncResponsiveEnhancements();

        // Update sticky bar price on variant change
        this.updateStickyBarPrice(variant);
      }

      // ========================================
      // MOBILE SWIPE FUNCTIONALITY
      // ========================================
      initMobileSwipe() {
        if (this._mobileSwipeInitialized || window.innerWidth >= 750) return;

        const mediaWrapper = this.elements.mediaWrapper;
        if (!mediaWrapper) {
          return;
        }

        if (!this.elements.thumbnailsContainer) {
          return;
        }

        // Cache scrollable bounds for performance
        this._thumbnailScrollBounds = {
          maxScroll: 0,
          containerWidth: 0
        };
        this._updateScrollBounds();

        // Store bound functions to enable proper removal
        this._boundTouchStart = this.handleTouchStart.bind(this);
        this._boundTouchMove = this.handleTouchMove.bind(this);
        this._boundTouchEnd = this.handleTouchEnd.bind(this);
        this._boundRAF = null;

        mediaWrapper.addEventListener('touchstart', this._boundTouchStart, { passive: true });
        mediaWrapper.addEventListener('touchmove', this._boundTouchMove, { passive: false });
        mediaWrapper.addEventListener('touchend', this._boundTouchEnd, { passive: true });
        this._mobileSwipeInitialized = true;
      }

      _updateScrollBounds() {
        if (!this.elements.thumbnailsContainer) return;
        this._thumbnailScrollBounds.maxScroll =
          this.elements.thumbnailsContainer.scrollWidth -
          this.elements.thumbnailsContainer.clientWidth;
        this._thumbnailScrollBounds.containerWidth = this.elements.thumbnailsContainer.clientWidth;
      }

      removeMobileSwipeListeners() {
        const mediaWrapper = this.elements.mediaWrapper;
        if (this._boundRAF) {
          cancelAnimationFrame(this._boundRAF);
          this._boundRAF = null;
        }
        if (!mediaWrapper) {
          this._mobileSwipeInitialized = false;
          return;
        }

        if (this._boundTouchStart) {
          mediaWrapper.removeEventListener('touchstart', this._boundTouchStart);
          this._boundTouchStart = null;
        }
        if (this._boundTouchMove) {
          mediaWrapper.removeEventListener('touchmove', this._boundTouchMove);
          this._boundTouchMove = null;
        }
        if (this._boundTouchEnd) {
          mediaWrapper.removeEventListener('touchend', this._boundTouchEnd);
          this._boundTouchEnd = null;
        }
        this._mobileSwipeInitialized = false;
      }

      handleTouchStart(e) {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
        this.isSwiping = false;

        // Update mobile state dynamically
        this.isMobile = window.innerWidth < 750;

        // Capture initial thumbnail scroll position for real-time tracking
        if (this.isMobile && this.elements.thumbnailsContainer) {
          this.thumbnailScrollStart = this.elements.thumbnailsContainer.scrollLeft;
          this.thumbnailBaseTransform = this.thumbnailScrollStart;
          // Store thumbnail width for smooth ratio calculation
          this._thumbnailWidth = this.elements.thumbnails[0]?.offsetWidth || 72;
          this._thumbnailGap = 12; // 0.75rem in pixels
          this._thumbnailUnit = this._thumbnailWidth + this._thumbnailGap;
          // Cancel any pending RAF
          if (this._boundRAF) {
            cancelAnimationFrame(this._boundRAF);
            this._boundRAF = null;
          }
        }
      }

      handleTouchMove(e) {
        if (!this.touchStartX || !this.touchStartY) return;

        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const diffX = Math.abs(touchX - this.touchStartX);
        const diffY = Math.abs(touchY - this.touchStartY);

        // Detect if this is a horizontal swipe
        if (diffX > diffY && diffX > 10) {
          this.isSwiping = true;

          // Real-time thumbnail tracking on mobile - use RAF for smooth performance
          if (window.innerWidth < 750 && this.elements.thumbnailsContainer) {
            // Cancel pending RAF to avoid stacking
            if (this._boundRAF) {
              cancelAnimationFrame(this._boundRAF);
            }

            // Schedule scroll update with smooth easing
            this._boundRAF = requestAnimationFrame(() => {
              const deltaX = touchX - this.touchStartX;
              const viewportWidth = window.innerWidth;

              // Calculate scroll ratio based on viewport and thumbnail unit
              // Full screen swipe = move by ~2.5 thumbnails for natural feel
              const scrollRatio = (this._thumbnailUnit * 2.5) / viewportWidth;
              const scrollOffset = deltaX * scrollRatio;

              const newScrollLeft = this.thumbnailBaseTransform - scrollOffset;
              const clampedScroll = Math.max(0, Math.min(this._thumbnailScrollBounds.maxScroll, newScrollLeft));

              this.elements.thumbnailsContainer.scrollLeft = clampedScroll;
              this._boundRAF = null;
            });
          }

          // Prevent default only for clear horizontal swipes
          e.preventDefault();
        }
      }

      handleTouchEnd(e) {
        if (!this.touchStartX || !this.touchStartY) return;

        // Cancel any pending RAF
        if (this._boundRAF) {
          cancelAnimationFrame(this._boundRAF);
          this._boundRAF = null;
        }

        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;

        this.handleSwipeGesture(touchEndX, touchEndY);

        // Reset
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.isSwiping = false;
      }

      handleSwipeGesture(endX, endY) {
        const diffX = endX - this.touchStartX;
        const diffY = endY - this.touchStartY;
        const minSwipeDistance = 30;

        // Check if horizontal swipe (more horizontal than vertical)
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > minSwipeDistance) {
          // Mark gallery as swiped to hide hint (Shopify best practice: don't show hint after interaction)
          this.classList.add('has-swiped');

          if (diffX > 0) {
            // Swipe right - previous image
            const newIndex = this.getAdjacentVisibleIndex(-1);
            if (newIndex !== this.currentIndex) {
              this.switchMedia(newIndex);
            }
          } else {
            // Swipe left - next image
            const newIndex = this.getAdjacentVisibleIndex(1);
            if (newIndex !== this.currentIndex) {
              this.switchMedia(newIndex);
            }
          }
        }
      }

      // ========================================
      // DOT INDICATORS
      // ========================================
      initDotIndicators() {
        if (!this.elements.dots.length) return;

        this.elements.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.switchMedia(index));
        });
      }

      updateDotIndicators() {
        if (!this.elements.dots.length) return;

        this.elements.dots.forEach((dot, index) => {
          dot.classList.toggle('vc-product__dot--active', index === this.currentIndex);
        });
      }

      // Override switchMedia to also update dots
      switchMedia(index) {
        if (index < 0 || index >= this.mediaData.length) {
          return;
        }

        this.currentIndex = index;
        const media = this.mediaData[index];

        // Update active thumbnail
        this.elements.thumbnails.forEach((thumb, i) => {
          const isActive = i === index;
          thumb.classList.toggle('vc-product__thumbnail--active', isActive);
          thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
          thumb.tabIndex = isActive && !thumb.hidden ? 0 : -1;
        });

        // Update dot indicators
        this.updateDotIndicators();

        // Auto-scroll thumbnails
        this.scrollToThumbnail(index);

        // Update main media
        this.updateMainMedia(media);

        // Preload adjacent images after the current selection has rendered.
        this.scheduleAdjacentPreload(index);

        // Update zoom if enabled
        if (this.zoomEnabled) {
          this.updateZoom();
        }

        // Dispatch custom event
        this.dispatchEvent(
          new CustomEvent('vc:media:changed', {
            bubbles: true,
            detail: { mediaId: media.id, index },
          })
        );
      }

      // ========================================
      // STICKY BAR
      // ========================================
      initStickyBar() {
        if (this._stickyBarInitialized || window.innerWidth >= 750) return;

        const sectionRoot = this.getSectionRoot();
        const stickyBar = sectionRoot.querySelector(`#vc-product-sticky-bar-${this.sectionId}`);
        const wishlistWrapper = sectionRoot.querySelector('.vc-product__wishlist-wrapper');
        const backToTop = document.querySelector(`#vc-back-to-top-${this.sectionId}`);
        if (!stickyBar) return;
        this._stickyBarElements = { stickyBar, wishlistWrapper, backToTop };
        let stickyBarVisible = stickyBar.classList.contains('vc-product__sticky-bar--visible');
        
        const getStickyThreshold = () => {
          const addToCartButton = sectionRoot.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          if (!addToCartButton) return 400;
          const rect = addToCartButton.getBoundingClientRect();
          return Math.max(280, window.scrollY + rect.top + rect.height);
        };

        let stickyBarThreshold = getStickyThreshold();
        const recalcThreshold = debounce(() => {
          stickyBarThreshold = getStickyThreshold();
        }, 120);
        window.addEventListener('resize', recalcThreshold);
        this._recalcStickyThreshold = recalcThreshold;

        const syncStickyState = (shouldShow) => {
          if (shouldShow === stickyBarVisible) return;
          stickyBarVisible = shouldShow;
          stickyBar.classList.toggle('vc-product__sticky-bar--visible', shouldShow);
          if (wishlistWrapper) {
            wishlistWrapper.classList.toggle('vc-product__wishlist-wrapper--shifted', shouldShow);
          }
          if (backToTop) {
            backToTop.classList.toggle('vc-back-to-top--shifted', shouldShow);
          }
        };

        const handleScroll = () => {
          syncStickyState(window.scrollY > stickyBarThreshold);
        };

        // Throttle scroll handler
        let ticking = false;
        this._onScroll = () => {
          if (!ticking) {
            window.requestAnimationFrame(() => {
              handleScroll();
              ticking = false;
            });
            ticking = true;
          }
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });

        // Sync sticky state on initial load and after first layout pass.
        this._onWindowLoad = () => {
          stickyBarThreshold = getStickyThreshold();
          handleScroll();
        };
        window.addEventListener('load', this._onWindowLoad, { once: true });
        window.requestAnimationFrame(() => {
          stickyBarThreshold = getStickyThreshold();
          handleScroll();
        });
        this._stickyBarInitialized = true;
      }

      destroyStickyBar() {
        if (this._recalcStickyThreshold) {
          window.removeEventListener('resize', this._recalcStickyThreshold);
          this._recalcStickyThreshold = null;
        }
        if (this._onScroll) {
          window.removeEventListener('scroll', this._onScroll);
          this._onScroll = null;
        }
        if (this._onWindowLoad) {
          window.removeEventListener('load', this._onWindowLoad);
          this._onWindowLoad = null;
        }

        if (this._stickyBarElements) {
          const { stickyBar, wishlistWrapper, backToTop } = this._stickyBarElements;
          stickyBar?.classList.remove('vc-product__sticky-bar--visible');
          wishlistWrapper?.classList.remove('vc-product__wishlist-wrapper--shifted');
          backToTop?.classList.remove('vc-back-to-top--shifted');
        }

        this._stickyBarInitialized = false;
      }

      updateStickyBarPrice(variant) {
        const sectionRoot = this.getSectionRoot();
        const stickyPriceEl = sectionRoot.querySelector(`#vc-sticky-price-${this.sectionId}`);
        const stickyCompareEl = sectionRoot.querySelector(`#vc-sticky-compare-${this.sectionId}`);
        const stickyButtonEl = sectionRoot.querySelector(`#vc-sticky-add-to-cart-${this.sectionId}`);

        if (!stickyPriceEl || !variant) return;

        // Update price
        stickyPriceEl.textContent = this.formatMoney(variant.price);
        if (stickyCompareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            stickyCompareEl.textContent = this.formatMoney(variant.compare_at_price);
            stickyCompareEl.classList.remove('hidden');
          } else {
            stickyCompareEl.textContent = '';
            stickyCompareEl.classList.add('hidden');
          }
        }

        // Update button state
        if (stickyButtonEl) {
          const addToCartText = window.variantStrings?.addToCart ||
            window.theme?.strings?.addToCart ||
            'Add to cart';
          const soldOutText = window.variantStrings?.soldOut ||
            window.theme?.strings?.soldOut ||
            'Sold out';
          stickyButtonEl.disabled = !variant.available;
          stickyButtonEl.textContent = variant.available
            ? addToCartText
            : soldOutText;
        }
      }

      formatMoney(cents) {
        // Use Shopify's formatMoney if available
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
        }
        // Fallback
        return '$' + (cents / 100).toFixed(2);
      }

      // Public method for external control
      setActiveMedia(mediaId) {
        const index = this.mediaData.findIndex((m) => m.id === String(mediaId));
        if (index >= 0) {
          this.switchMedia(index);
        }
      }
    }
  );
}
